import { useQuery } from '@tanstack/react-query'

import {
  integrationSetupJobsListOptions,
  SetupJobStatusEnum,
  type SetupJob
} from 'src/api/main'

const REFETCH_INTERVAL = 1500

export type SetupProgress = {
  jobs: SetupJob[]
  total: number
  finished: number
  percent: number
  hasError: boolean
  allDone: boolean
  isFetching: boolean
}

/**
 * Polls the build jobs of one setup run until they settle.
 *
 * Shared by the stepper modal and by whatever wants to show the headline number
 * without opening it. Two callers polling the same key would otherwise drift
 * apart on the stop condition; here they share both the request and the cache
 * entry, so opening the modal shows a populated list immediately.
 */
export const useSetupProgress = (
  parentId: number | undefined,
  enabled = true
): SetupProgress => {
  const { data, isFetching } = useQuery({
    ...integrationSetupJobsListOptions({
      query: { parent: Number(parentId), per_page: 200 }
    }),
    enabled: Boolean(parentId) && enabled,
    refetchInterval: (queryData) => {
      const results = queryData.state.data?.results ?? []
      const hasError = results.some(
        ({ status }) => status === SetupJobStatusEnum.FAILED
      )
      const allDone = results.every(
        ({ status }) => status === SetupJobStatusEnum.FINISHED
      )

      return !hasError && !allDone ? REFETCH_INTERVAL : false
    }
  })

  const jobs = data?.results ?? []
  const total = jobs.length
  const finished = jobs.filter(
    (job) => job.status === SetupJobStatusEnum.FINISHED
  ).length

  return {
    jobs,
    total,
    finished,
    percent: total > 0 ? Math.round((finished / total) * 100) : 0,
    hasError: jobs.some(({ status }) => status === SetupJobStatusEnum.FAILED),
    // An empty list is "nothing reported yet", not "everything is done" — the
    // build enqueues its steps a moment after the job itself exists.
    allDone: total > 0 && finished === total,
    isFetching
  }
}
